import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import Joi from 'joi';
import cx from 'classnames';
import sanitizeHtml from 'sanitize-html';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import auth from '../../services/authService';
import { useGlobalContext } from '../../context/GlobalContext';
import { getCategories } from '../../services/fakeCategoryService';
import { getTeam } from '../../services/teamService';
import { scrollTop } from '../../utils/utilities';
import { formatNumber } from '../../utils/format';
import { extractYouTubeVideoId, fetchYouTubeTitle } from '../../utils/youtube';
import theme from '../../utils/theme';
import Icon from '../common/Icon';
import NoContent from '../common/NoContent';
import ArtistInput from '../common/ArtistInput';
import songService from '../../services/songService';
import ToggleChip from '../common/ToggleChip';
import useForm from '../../hooks/useForm';
import useKeyProgressionPreview from '../../hooks/useKeyProgressionPreview';
import useSpacePlay from '../../hooks/useSpacePlay';
import SongParts from '../SongParts';
import IntensityPanel, { canArrange } from '../IntensityPanel';
import { escapeCancelsEdit, withOpenPartKept } from '../../utils/songFormEditor';
import SongPartsGridModal from '../SongParts/SongPartsGridModal';
import Footer from '../common/Footer';
import LyricsActions from '../LyricsActions/LyricsActions';
import {
  handleBoldButton,
  handleItalicButton,
  handleUnderlineButton,
  transposeChordText,
} from '../../helpers/lyricsActions';
import RepeatTimerModal from '../modals/RepeatTimerModal';
import ChordBarsModal from '../modals/ChordBarsModal';
import InstrumentRow from '../common/InstrumentRow';
import RhythmStylePicker from '../common/RhythmStylePicker';
import PromptModal from '../modals/PromptModal';
import AssignPartModal from '../modals/AssignPartModal';
import PartMidiUploadModal from '../modals/PartMidiUploadModal';
import ChordSuggestions from '../ChordSuggestions';
import ThemeSuggestions from '../ThemeSuggestions';
import TagInput from '../common/TagInput';
import { renderInput, renderSelect, renderInputNumber, renderPartTypeSelect } from '../common/renderUtils';
import { getTransposeKeys, calculateSemitones, getKeyAfterTranspose } from '../../utils/autoTranspose';
import { getPartKeyOptions, getPartKeys, getSongEndKey } from '../../utils/partKey';
import { getPartBpm } from '../../utils/partTempo';
import { getKeyRootDistance } from '../../utils/keyDistance';
import { detectThemesInLyrics } from '../../utils/themeDetection';
import { findChordLines } from '../../utils/chordBars';
import { getArrangement, applyArrangement } from '../../utils/arrangeHistory';
import {
  continuesTypingRun,
  EMPTY_HISTORY,
  getHistoryShortcut,
  pushHistory,
  redoHistory,
  undoHistory,
} from '../../utils/editHistory';
import { hasChartData, importLyricsChart, mapChartDataChords } from '../../utils/chartData';
import { getBeatsPerBar, getRepeatCount } from '../../utils/songTiming';
import { getMidiTranspositionToKey, hasMidi } from '../../utils/midiJson';
import { DEFAULT_INSTRUMENT_ID, getPartInstrumentId } from '../../utils/midiInstruments';
import { BLOCK_CHORDS, toRhythmId } from '../../utils/chordRhythmPatterns';
import { getAttachedToPartIds, isAttachedToPart, isNotePart } from '../../utils/notes';
import {
  canAttachChart,
  canChart as canChartPart,
  getInstrumentLabels,
  hasPartContent,
  isInstrumentalPart,
} from '../../utils/parts';
import { FREE_SONG_LIMIT, SUBSCRIPTION_ENABLED } from '../../utils/subscription';
import SongCounter from '../subscription/SongCounter';
import UpgradeModal from '../subscription/UpgradeModal';

import songKeys from '../../data/keys.json';
import songParts from '../../data/songParts.json';
import instruments from '../../data/instruments.json';
import {
  computePartNumbers,
  getNextPartNumber,
  getSectionEndIndex,
  isSectionPart,
  renumberParts,
  sectionIndexAt,
} from '../../utils/partNumbers';
import { suggestIntensities } from '../../utils/suggestedLevels';
import timeSignatures from '../../data/timeSignatures.json';

import { ReactComponent as PeopleIcon } from '../../icons/group.svg';
import { ReactComponent as GridIcon } from '../../icons/dots-grid3x3.svg';
import { ReactComponent as XmarkCircleIcon } from '../../icons/xmark-circle.svg';
import { ReactComponent as PageStarIcon } from '../../icons/page-star.svg';
import { ReactComponent as IntensityIcon } from '../../icons/intensity.svg';
import { ReactComponent as RepeatIcon } from '../../icons/repeat.svg';
import { ReactComponent as SaveIcon } from '../../icons/floppy-disk.svg';

import PageBar from '../PageBar';
import {
  MOBILE_TAB_QUERY,
  ownsSwipe,
  selectedText,
  startingTab,
  swipedTab,
  tabsNeedingAttention,
} from './songFormTabs';
import './SongForm.scss';

const MAX_LYRICS_HISTORY = 100;

// Matches the part-flash CSS animation duration (0.4s) times its 2 iterations
const PART_FLASH_DURATION = 800;

// Helper function to map song to view model
const mapToViewModel = (song) => {
  const currentPart = 'Intro';
  const partNumber = getNextPartNumber(song.parts, currentPart);

  return {
    ...songService.toSongBody(song),
    currentPart: currentPart,
    currentLyrics: '',
    partNumber: partNumber,
  };
};

// The duplicate count in the repeat prompt, icon first; Trans hands it the translated words
const Duplicates = ({ children }) => (
  <span className="duplicates">
    <RepeatIcon className="duplicate-icon" />
    {children}
  </span>
);

const SongForm = (props) => {
  const { t } = useTranslation();
  // Stored part names are English; this is how one is shown
  const partName = (name) => t(`parts.${name}`, { defaultValue: name });
  const location = useLocation();
  const artists = location.state?.artists || [];
  const { songs, setSongs, dataLoaded, allThemes } = useGlobalContext();

  const initialData = {
    title: '',
    artist: '',
    key: '',
    transposeBy: '',
    bpm: '',
    timeSignature: '4/4',
    categoryId: '',
    youtube: '',
    favorite: false,
    parts: [],
    teamId: '',
    currentPart: 'Intro',
    currentLyrics: '',
    partNumber: 1,
    currentRepeatCount: 1,
    isPublic: true,
    isHidden: false,
    isDraft: false,
    isNewSong: false,
    themes: [],
  };

  const [categories, setCategories] = useState([]);
  const [keys, setKeys] = useState([]);
  const [transposeKeys, setTransposeKeys] = useState([]);
  const [songPartsData, setSongPartsData] = useState([]);
  const [timeSignaturesData, setTimeSignaturesData] = useState([]);
  const [editPartId, setEditPartId] = useState(null);
  // For the song load, which closes over the first render's editPartId
  const editPartIdRef = useRef(null);
  editPartIdRef.current = editPartId;
  const [flashingPart, setFlashingPart] = useState(null);
  const [savingSong, setSavingSong] = useState(false);
  const [loadingSong, setLoadingSong] = useState(true);
  const [isGridModalOpen, setIsGridModalOpen] = useState(false);
  const [overview, setOverview] = useState(false);
  // Setting how hard parts play through, and the parts picked to set it on
  const [arranging, setArranging] = useState(false);
  const [arrangeIds, setArrangeIds] = useState([]);
  // A part being edited with unsaved changes, held while the admin says whether to drop them to
  // open it in the intensity panel
  const [pendingIntensityPart, setPendingIntensityPart] = useState(null);
  // The intensity panel's undo, kept only while it is open
  const [arrangeHistory, setArrangeHistory] = useState(EMPTY_HISTORY);
  const [isTimerModalOpen, setIsTimerModalOpen] = useState(false);
  const [isChordBarsModalOpen, setIsChordBarsModalOpen] = useState(false);
  // The instrument or rhythm sheet of the song's chart defaults, which wakes their preview
  const [isChartSheetOpen, setIsChartSheetOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assigningPart, setAssigningPart] = useState(null);
  const [teamUsers, setTeamUsers] = useState([]);
  const [pendingAssignedUsers, setPendingAssignedUsers] = useState([]);
  const [pendingInstruments, setPendingInstruments] = useState([]);
  const [pendingSoloInstruments, setPendingSoloInstruments] = useState([]);
  const [isMidiModalOpen, setIsMidiModalOpen] = useState(false);
  const [midiPart, setMidiPart] = useState(null);
  // The key the song was loaded with. data.key follows the field as the user edits it, so it
  // can't anchor a MIDI clip that predates the sourceKey field — this is what it was uploaded as.
  const [loadedSongKey, setLoadedSongKey] = useState('');
  const [pendingMidi, setPendingMidi] = useState(null);
  // The instrument the part being edited auditions its chord chart with. Held here rather than
  // written straight onto the part because the chart itself isn't on the part yet either — it is
  // held below until Add Part commits both together.
  const [chartInstrumentId, setChartInstrumentId] = useState(null);
  // The rhythm it plays with over the song's, held the same way. Null follows the song.
  const [chartRhythm, setChartRhythm] = useState(null);
  // The chart of the part being written, as chartData. Held beside chartInstrumentId and committed
  // with it, so backing out of the editor leaves the part charted as it was.
  const [pendingChart, setPendingChart] = useState(null);
  // The key of the part being written, when it is not the song's — a bridge that goes up. Empty
  // means the part follows the song, which is what nearly every part does.
  const [pendingPartKey, setPendingPartKey] = useState('');
  const [pendingPartBpm, setPendingPartBpm] = useState('');
  // A key or transpose edit waiting on the admin's confirmation, held here until they take it
  const [pendingKeyWarning, setPendingKeyWarning] = useState(null);
  const [suggestedThemes, setSuggestedThemes] = useState([]);
  const [songCount, setSongCount] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [timesPlayed, setTimesPlayed] = useState(0);
  // A cross-section drag that would renumber parts is held here pending confirmation.
  const [pendingMove, setPendingMove] = useState(null);
  // A repeat change on a part that has duplicates, held until the admin says whether the
  // duplicates should follow it.
  const [pendingRepeatSpread, setPendingRepeatSpread] = useState(null);
  // An edit to the lyrics of a part carrying a chart, held until the admin says whether the chart
  // still describes them.
  const [pendingChartUpdate, setPendingChartUpdate] = useState(null);
  // Bumped to remount the parts list, discarding SortableJS's DOM move when a drag is cancelled.
  const [listRefreshKey, setListRefreshKey] = useState(0);

  // Whether the stale-chart warning has been settled for the edit in the editor — by keeping the
  // chart, or by saving a new one out of the builder. A ref rather than state because nothing
  // renders off it: it only has to survive the trip out to the builder and back, so that the press
  // of Update Part that follows doesn't ask again about the answer just given.
  const chartWarningAnswered = useRef(false);
  // Bar lines typed into the lyrics of an instrumental that already has a chart, held until the
  // admin says they should replace it — and whether they have, so Add Part doesn't ask twice
  const [pendingLyricsChart, setPendingLyricsChart] = useState(null);
  const lyricsChartAnswered = useRef(false);

  const lyricsUndoStack = useRef([]);
  const lyricsRedoStack = useRef([]);
  // When the open typing run last took a keystroke; null once anything else ends the run
  const lyricsTypedAt = useRef(null);
  const flashTimerRef = useRef(null);

  // Clear any pending part-flash timeout on unmount so it can't set state after the fact
  useEffect(() => () => clearTimeout(flashTimerRef.current), []);

  const isNewSong = props.match.params.id === 'new';
  const [mobileTab, setMobileTab] = useState(() => startingTab(isNewSong, location.search));
  // Each tab's scroll, so coming back to one lands where it was left
  const tabScroll = useRef({});
  // Where a touch that could turn into a swipe between tabs began
  const swipeStart = useRef(null);
  // A part added from the Lyrics tab, flashed once the Parts tab is opened to show where it landed
  const addedPartId = useRef(null);
  const isAdmin = !!auth.getCurrentUser()?.isAdmin;
  const showCounter = SUBSCRIPTION_ENABLED && isNewSong && isAdmin && songCount !== null && songCount < FREE_SONG_LIMIT;

  const schema = {
    _id: Joi.string(),
    title: Joi.string().max(60).required().label('Title'),
    artist: Joi.string().max(50).required().label('Artist'),
    key: Joi.string().required().min(1).max(7).label('Key'),
    transposeBy: Joi.number().min(-11).max(11).allow('').allow(null).label('Transpose By'),
    // Required: without a tempo Smart Scroll can't turn a part's bars into seconds, so it falls
    // back to a default pace and every length the chart states stops counting — see songTiming.js.
    // The range matches the metronome's own (TempoControl), and 0 is excluded because a zero BPM
    // is the same missing tempo wearing a number.
    bpm: Joi.number()
      .min(20)
      .max(300)
      .required()
      .label('BPM')
      .messages({ 'number.base': t('songForm.bpmRequired'), 'any.required': t('songForm.bpmRequired') }),
    timeSignature: Joi.string().required().label('Time Signature'),
    categoryId: Joi.string().required().label('Category'),
    youtube: Joi.string().max(100).allow('').label('YouTube'),
    chartInstrumentId: Joi.string().max(50).allow(null),
    chartRhythm: Joi.string().max(50).allow(null),
    chartEffects: Joi.object().allow(null),
    favorite: Joi.boolean().label('Favorite'),
    parts: Joi.array().min(1).required().label('Parts'),
    teamId: Joi.string(),
    currentLyrics: Joi.string().allow('').label('Lyrics and Chords'),
    currentPart: Joi.string().label('Parts'),
    partNumber: Joi.number().label('Number'),
    currentRepeatCount: Joi.number().label('Repeat'),
    isPublic: Joi.boolean().required(),
    isHidden: Joi.boolean().required(),
    isDraft: Joi.boolean().required(),
    isNewSong: Joi.boolean(),
    isCopy: Joi.boolean(),
    originalTeam: Joi.object(),
    themes: Joi.array().items(Joi.string().trim().max(30)).max(10).label('Themes'),
  };

  const { data, setData, errors, setErrors, validatePart, handleChange, handleSubmit, validate } = useForm(
    initialData,
    schema,
  );
  const validationErrors = validate();
  const tabAttention = tabsNeedingAttention({ validationErrors, editPartId });
  // A chart's bars follow the song's time signature
  const beatsPerBar = getBeatsPerBar(data.timeSignature);

  // Each time the intensity panel opens or closes, its undo starts over
  useEffect(() => setArrangeHistory(EMPTY_HISTORY), [arranging]);

  useEffect(() => {
    const populateCategories = () => {
      setCategories(getCategories());
    };

    const populateSongParts = () => {
      setSongPartsData(songParts);
    };

    const populateTimeSignatures = () => {
      setTimeSignaturesData(timeSignatures);
    };

    const populateSongKeys = () => {
      setKeys(songKeys);
      setTransposeKeys([]);
    };

    const populateSong = async () => {
      try {
        const songId = props.match.params.id;
        if (songId === 'new') {
          // New song: set team id
          const newData = {
            title: '',
            artist: '',
            key: '',
            bpm: '',
            timeSignature: '4/4',
            categoryId: '',
            youtube: '',
            favorite: false,
            parts: [],
            teamId: auth.getCurrentUser().teamId,
            currentPart: 'Intro',
            currentLyrics: '',
            partNumber: 1,
            currentRepeatCount: 1,
            isPublic: true,
            isHidden: false,
            isDraft: false,
            isNewSong: false,
          };

          setData(newData);
          setLoadedSongKey('');

          return true;
        }

        // Existing song
        const song = await songService.getSong(songId);

        // Load duplicate parts
        song.parts = songService.loadDuplicateParts(song);

        // Deny access to edit public songs from another teams
        if (song.team._id !== auth.getCurrentUser().teamId) throw new Error('Forbidden');

        setData((current) => withOpenPartKept(mapToViewModel(song), current, !!editPartIdRef.current));
        setLoadedSongKey(song.key || '');
        setTimesPlayed(song.stats?.timesPlayed || 0);
        return true;
      } catch (ex) {
        props.history.replace('/not-found');
        return false;
      }
    };

    const initializeComponent = async () => {
      if (isNewSong) {
        if (SUBSCRIPTION_ENABLED && songs.length >= FREE_SONG_LIMIT) {
          props.history.replace('/songs');
          return;
        }

        setSongCount(songs.length);
      }

      populateCategories();
      populateSongParts();
      populateTimeSignatures();
      populateSongKeys();

      const success = await populateSong();

      if (success) {
        try {
          const currentUser = auth.getCurrentUser();

          if (currentUser?.teamId) {
            const { data: teamData } = await getTeam(currentUser.teamId);
            setTeamUsers(teamData.users ?? []);
          }
        } catch (_) {
          // non-critical: team users used for assignment feature
        }
        scrollTop();
        setLoadingSong(false);
      }
    };

    initializeComponent();
  }, [props.history, props.match.params.id, isNewSong, songs.length, setData]);

  useEffect(() => {
    return () => {
      if (savingSong) setSavingSong(false);
    };
  }, [savingSong]);

  // Update transpose keys when the song key changes
  useEffect(() => {
    if (data.key) {
      setTransposeKeys(getTransposeKeys(data.key));
    } else {
      setTransposeKeys([]);
    }
  }, [data.key]);

  // Automatically recalculate partNumber when currentPart or parts change
  // but only when not editing an existing part
  useEffect(() => {
    if (data.parts && data.currentPart && !editPartId) {
      const expectedPartNumber = getNextPartNumber(data.parts, data.currentPart);

      // Only update part number when changing part type or when parts change
      // Don't override manual user input from number controls
      if (!data.partNumber || data.partNumber < 1) {
        setData((prevData) => ({
          ...prevData,
          partNumber: Math.max(1, expectedPartNumber),
        }));
      }
    }
  }, [data.currentPart, data.parts, editPartId, setData, data.partNumber]);

  // Detect themes in current lyrics when allThemes becomes available or lyrics change
  useEffect(() => {
    if (allThemes.length > 0 && data.currentLyrics) {
      const detected = detectThemesInLyrics(data.currentLyrics, allThemes);
      setSuggestedThemes(detected);
    } else {
      setSuggestedThemes([]);
    }
  }, [allThemes, data.currentLyrics]);

  const showTab = (tab, { fromTop = false } = {}) => {
    tabScroll.current[mobileTab] = window.scrollY;
    if (fromTop) tabScroll.current[tab] = 0;
    setMobileTab(tab);

    if (tab === 'parts' && addedPartId.current) {
      const id = addedPartId.current;

      addedPartId.current = null;
      setFlashingPart({ id, type: 'duplicate' });
      flashTimerRef.current = setTimeout(() => {
        setFlashingPart((prev) => (prev?.id === id ? null : prev));
      }, PART_FLASH_DURATION);
    }
  };

  // A portal's touches bubble through React into the form, so a modal's swipes are ruled out by the DOM
  const handleSwipeStart = (e) => {
    const touch = e.touches[0];
    const fromForm = e.currentTarget.contains(e.target) && !ownsSwipe(e.target, e.currentTarget);

    swipeStart.current =
      e.touches.length === 1 && fromForm && window.matchMedia(MOBILE_TAB_QUERY).matches
        ? { x: touch.clientX, y: touch.clientY, at: e.timeStamp, target: e.target }
        : null;
  };

  const handleSwipeEnd = (e) => {
    if (!swipeStart.current) return;

    const touch = e.changedTouches[0];
    const { x, y, at, target } = swipeStart.current;
    const tab = swipedTab(mobileTab, touch.clientX - x, touch.clientY - y, e.timeStamp - at);

    swipeStart.current = null;
    if (tab && !selectedText(target)) showTab(tab);
  };

  // Lands the new tab where it was left; tabs only exist on a phone, so a desktop page never moves
  useLayoutEffect(() => {
    if (!window.matchMedia(MOBILE_TAB_QUERY).matches) return;

    window.scrollTo(0, tabScroll.current[mobileTab] || 0);
  }, [mobileTab]);

  // When loading finishes, check for an editPartId URL param and open that part in edit mode
  useEffect(() => {
    if (loadingSong) return;

    const params = new URLSearchParams(location.search);
    const partIdFromUrl = params.get('editPartId');
    const intensityPartId = params.get('intensityPartId');

    if (!partIdFromUrl && !intensityPartId) return;

    const part = data.parts?.find((p) => p.id === (partIdFromUrl || intensityPartId));

    if (part) {
      if (partIdFromUrl) handlePartEdit(part);
      else if (canArrange(part)) openInIntensity(part);

      props.history.replace({ pathname: `/songs/${props.match.params.id}`, state: props.location.state });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSong]);

  const handleAddPart = () => {
    let { currentLyrics, currentPart, partNumber } = data;

    // Sanitize html in lyrics
    currentLyrics = sanitizeHtml(currentLyrics, {
      allowedTags: [],
      allowedAttributes: {},
    });

    const part = { currentLyrics, partNumber };

    // Validate. A charted part can be all chart: an instrumental's chords live in its chart now, not
    // its lyrics.
    const validationErrors = validatePart(part, {
      lyricsOptional: canChartPart(currentPart) && hasChartData(pendingChart),
    });
    setErrors(validationErrors || {});

    if (validationErrors) return;

    addPart(currentPart, currentLyrics.trimEnd());
  };

  /**
   * Writes the chart's instrument and rhythm onto a part where it sets them over the song's, or
   * takes them off one that follows the song or no longer has a chart.
   *
   * Deleting rather than storing them anyway is what keeps the properties meaning something: they
   * are the sound of a chart, so a part whose chart has been removed has no use for them, and one
   * that follows the song has to change with it.
   */
  const applyChartInstrument = (part, chart) => {
    if (chartInstrumentId && chart) part.chartInstrumentId = chartInstrumentId;
    else delete part.chartInstrumentId;

    if (chartRhythm && chart) part.chartRhythm = chartRhythm;
    else delete part.chartRhythm;
  };

  /**
   * A repeat is the part's own — a duplicate can be played a different number of times than the
   * part it mirrors. So when an edit changes the repeat on a part that has duplicates, ask
   * instead of guessing; the answer comes back as `spreadRepeat` on a second call, and nothing
   * is touched until then.
   */
  const addPart = (name, typedLyrics, spreadRepeat) => {
    const newData = { ...data };
    const { parts, partNumber, currentRepeatCount } = newData;
    const repeatCount = currentRepeatCount > 1 ? currentRepeatCount : 1;
    // Bar lines typed into an instrumental's lyrics become its chart, and the lyrics keep the words
    const typed = importLyricsChart({ name, lyrics: typedLyrics }, beatsPerBar);
    const { lyrics } = typed;
    // Only a part with chords to chart has anywhere to keep one — retyped as a Note, it goes
    const chart = canChartPart(name) ? typed.chartData || pendingChart : null;

    // Typed bar lines replace the chart the part had, which the lyrics box doesn't show, so ask first
    if (typed.chartData && pendingChart && !lyricsChartAnswered.current) {
      setPendingLyricsChart({ name, lyrics: typedLyrics, label: `${partName(name)} ${partNumber}` });

      return;
    }

    if (editPartId && !chartWarningAnswered.current) {
      const editedPart = parts.find((p) => p.id === editPartId);
      const editedChart = editedPart?.chartData;

      // A chart kept off the lyrics is written alongside the words rather than read out of them, so
      // an edit can leave it describing a part that no longer exists. Nothing here can tell whether
      // it still fits — only whether the words moved — so the answer is the admin's. A chart the
      // retype above is dropping anyway isn't worth asking about.
      if (canAttachChart(name) && chart && hasChartData(editedChart) && lyrics !== editedPart.lyrics) {
        setPendingChartUpdate({ name, lyrics: typedLyrics, label: `${partName(name)} ${partNumber}` });

        return;
      }
    }

    if (editPartId && spreadRepeat === undefined) {
      const editedPart = parts.find((p) => p.id === editPartId);
      const duplicates = editedPart?.hasDuplicates
        ? parts.filter((p) => p.isDuplicate && p.duplicateId === editedPart.id)
        : [];

      if (duplicates.length > 0 && repeatCount !== (editedPart.repeatCount || 1)) {
        setPendingRepeatSpread({
          name,
          lyrics: typedLyrics,
          repeatCount,
          duplicateCount: duplicates.length,
          label: `${partName(name)} ${partNumber}`,
        });

        return;
      }
    }

    if (editPartId) {
      // Edit part
      const part = parts.find((p) => p.id === editPartId);

      part.name = name;
      part.isHidden = name === 'Hidden';
      part.number = partNumber;
      part.lyrics = lyrics;
      part.repeatCount = repeatCount;
      const soundBefore = _.pick(part, ['chartInstrumentId', 'chartRhythm']);
      applyChartInstrument(part, chart);
      const soundChanged = !_.isEqual(soundBefore, _.pick(part, ['chartInstrumentId', 'chartRhythm']));

      // Absent rather than empty, the way midi and the part key are: a part with no chart of its
      // own should carry no trace of one
      if (chart) part.chartData = chart;
      else delete part.chartData;

      // Absent rather than empty for a part that declares nothing: the backend's part schema takes
      // a key of 1-7 characters or none at all, and inheriting is what nearly every part does. A
      // duplicate never declares one at all — it reads the key in force where it sits.
      if (pendingPartKey && !part.isDuplicate) part.key = pendingPartKey;
      else delete part.key;

      // The same for a tempo: absent means the song's
      if (pendingPartBpm && !part.isDuplicate) part.bpm = Number(pendingPartBpm);
      else delete part.bpm;

      // Only notes can be attached to other parts, so retyping one as a real part drops its
      // attachments rather than leaving them behind as dead weight
      if (!isNotePart(part)) delete part.attachedToPartIds;

      // Apply changes to duplicates, if any. Only lyrics/chords are mirrored — instruments and
      // assigned members are the duplicate's own once it exists, a duplicate never carries a key
      // of its own at all, and the repeat follows only when the admin asked for it in the prompt
      // above.
      if (part.hasDuplicates) {
        parts.forEach((p) => {
          if (p.isDuplicate && p.duplicateId === part.id) {
            p.name = name;
            p.number = partNumber;
            p.lyrics = lyrics;
            p.midi = part.midi;
            if (chart) p.chartData = chart;
            else delete p.chartData;
            // A duplicate can be given a sound of its own in the intensity panel, so only a change
            // to the original's, or its chart going, reaches it
            if (soundChanged || !chart) applyChartInstrument(p, chart);
            if (spreadRepeat) p.repeatCount = repeatCount;
          }
        });
      }

      // Editing a duplicate part
      if (part.isDuplicate) {
        const originalPart = parts.find((p) => p.id === part.duplicateId);
        // If there are any changes, it becomes an original part
        if (
          part.name !== originalPart.name ||
          part.number !== originalPart.number ||
          part.lyrics !== originalPart.lyrics ||
          part.midi !== originalPart.midi ||
          !_.isEqual(part.chartData, originalPart.chartData)
        ) {
          delete part.isDuplicate;
          delete part.duplicateId;

          // Check if the original part has more duplicates
          const duplicates = parts.filter((p) => p.duplicateId === originalPart.id);

          if (duplicates.length === 0) {
            const index = parts.findIndex((p) => p.id === originalPart.id);
            parts[index].hasDuplicates = false;
          }
        }
      }

      const index = parts.findIndex((p) => p.id === part.id);
      parts.splice(index, 1, part);
      setPendingAssignedUsers([]);
      setPendingInstruments([]);
      setPendingSoloInstruments([]);
      setPendingMidi(null);
      setChartInstrumentId(null);
      setChartRhythm(null);
      setPendingChart(null);
      setPendingPartKey('');
      setPendingPartBpm('');
      chartWarningAnswered.current = false;
      lyricsChartAnswered.current = false;
    } else {
      // Add part
      const id = Date.now().toString();

      // On a phone the list is on another tab, so say where the part went
      if (window.matchMedia(MOBILE_TAB_QUERY).matches) {
        addedPartId.current = id;
        toast.info(t('songForm.addedToParts'), { autoClose: 2000, hideProgressBar: true });
      }

      parts.push({
        id,
        name,
        isHidden: name === 'Hidden',
        number: partNumber,
        lyrics,
        repeatCount,
        ...(pendingAssignedUsers.length > 0 && { assignedUsers: pendingAssignedUsers }),
        ...(pendingInstruments.length > 0 && { assignedInstruments: pendingInstruments }),
        ...(pendingSoloInstruments.length > 0 && { soloInstruments: pendingSoloInstruments }),
        ...(pendingMidi && { midi: pendingMidi }),
        ...(chart && { chartData: chart }),
        ...(chartInstrumentId && chart && { chartInstrumentId }),
        ...(chartRhythm && chart && { chartRhythm }),
        ...(pendingPartKey && { key: pendingPartKey }),
        ...(pendingPartBpm && { bpm: Number(pendingPartBpm) }),
      });
      setPendingAssignedUsers([]);
      setPendingInstruments([]);
      setPendingSoloInstruments([]);
      setPendingMidi(null);
      setChartInstrumentId(null);
      setChartRhythm(null);
      setPendingChart(null);
      setPendingPartKey('');
      setPendingPartBpm('');
      chartWarningAnswered.current = false;
      lyricsChartAnswered.current = false;
    }

    setEditPartId(null); // reset editPartId
    newData.currentLyrics = ''; // reset currentLyrics
    newData.currentRepeatCount = 1; // reset repeat count
    newData.parts = parts;
    clearLyricsHistory();

    // Update partNumber for current partName
    const updatedData = { ...newData };

    updatedData.partNumber = getNextPartNumber(newData.parts, newData.currentPart);
    setData(updatedData);

    // Clear suggestions when lyrics are cleared after adding a part
    setSuggestedThemes([]);
  };

  const handlePartMove = (part, direction) => {
    const newData = { ...data };
    const { parts } = newData;

    const currentIndex = parts.indexOf(part);
    const destination = direction === 'down' ? currentIndex + 1 : currentIndex - 1;
    const sectionBefore = sectionIndexAt(parts, currentIndex);

    parts.splice(currentIndex, 1);
    parts.splice(destination, 0, part);

    renumberOnSectionChange(parts, part, sectionBefore, destination);

    newData.parts = [...parts];
    setData(newData);
  };

  // A part keeps its number when reordered within its section; it's only renumbered
  // when it crosses into a different section (or when a Section marker itself moves,
  // which reshuffles every following part's section). `sectionBefore` is the part's
  // section index measured on the pre-move array; `newIndex` is its position in the
  // post-move array. Notes are excluded — they're always renumbered globally via
  // sortNoteParts below.
  const renumberOnSectionChange = (parts, movedPart, sectionBefore, newIndex) => {
    const crossedSection = isSectionPart(movedPart) || sectionBefore !== sectionIndexAt(parts, newIndex);

    if (crossedSection) renumberParts(parts);
    sortNoteParts(parts);
  };
  const sortNoteParts = (parts) => {
    let notePartCount = 0;

    parts.forEach((part) => {
      if (part.name === 'Note') {
        notePartCount += 1;
        part.number = notePartCount;
      }
    });
  };

  // Pure: returns a fresh parts array with the move applied and renumbered exactly as a
  // commit would, plus whether the move crossed a section (so it actually renumbers) and
  // the moved part's id. Works on a deep clone so the live parts are never mutated — the
  // caller can preview the result before deciding to commit it.
  const computeMovedParts = (sourceParts, oldIndex, newIndex) => {
    const parts = _.cloneDeep(sourceParts);
    const movedPart = parts[oldIndex];
    const sectionBefore = sectionIndexAt(parts, oldIndex);

    parts.splice(oldIndex, 1);
    parts.splice(newIndex, 0, movedPart);

    const crossedSection = isSectionPart(movedPart) || sectionBefore !== sectionIndexAt(parts, newIndex);

    if (crossedSection) renumberParts(parts);
    sortNoteParts(parts);

    return { parts, crossedSection, movedId: movedPart.id };
  };

  // The renumbering the user actually cares about for this move: parts in the section the
  // part is dropped into, plus any duplicate elsewhere whose original lives in that section
  // (duplicates mirror their original's number, so they ripple). `renumberParts` rewrites
  // numbers for the whole song, so we deliberately ignore unrelated sections it happens to
  // touch. Numbers are compared numerically — stored values can be strings ("1") while
  // renumberParts writes numbers (1), and "1" === 1 should not count as a change.
  const buildRenumberDiff = (beforeParts, afterParts, movedId) => {
    const before = computePartNumbers(beforeParts);
    const after = computePartNumbers(afterParts);

    // Section index for every position in the post-move array: a Section marker and the
    // parts following it (until the next marker) share the same index.
    const sectionIndexByPos = [];
    const posById = {};
    let sectionIndex = 0;

    afterParts.forEach((part, i) => {
      if (isSectionPart(part)) sectionIndex += 1;
      sectionIndexByPos[i] = sectionIndex;
      posById[part.id] = i;
    });

    const sectionIndexOfId = (id) => (posById[id] != null ? sectionIndexByPos[posById[id]] : null);
    const destSectionIndex = sectionIndexOfId(movedId);

    const numberChanged = (id) => Number(before[id]) !== Number(after[id]);

    return afterParts
      .filter((part) => {
        if (isSectionPart(part)) return false;
        // The moved part is always shown — even if its own number is unchanged — to anchor
        // the preview in the part the user is actually dragging.
        if (part.id === movedId) return true;
        if (!numberChanged(part.id)) return false;

        const inDestSection = sectionIndexOfId(part.id) === destSectionIndex;
        const isDuplicateOfDestSection = part.isDuplicate && sectionIndexOfId(part.duplicateId) === destSectionIndex;

        return inDestSection || isDuplicateOfDestSection;
      })
      .map((part) => ({
        id: part.id,
        name: part.name,
        from: before[part.id],
        to: after[part.id],
        isMoved: part.id === movedId,
        isDuplicate: !!part.isDuplicate,
        sectionName: getSectionNameForId(afterParts, part.id),
      }));
  };

  // Title (stored in lyrics) of the Section the part now sits under, or null if it's before
  // the first Section marker.
  const getSectionNameForId = (parts, partId) => {
    const index = parts.findIndex((p) => p.id === partId);

    for (let i = index; i >= 0; i -= 1) {
      if (isSectionPart(parts[i])) return parts[i].lyrics;
    }
    return null;
  };

  // Used by the grid modal: commit a reorder directly.
  const handlePartSort = (oldIndex, newIndex) => {
    if (oldIndex === newIndex) return;

    const { parts } = computeMovedParts(data.parts, oldIndex, newIndex);
    setData({ ...data, parts });
  };

  // Used by the main parts list: a within-section reorder commits immediately, but a move
  // that crosses a section (and so renumbers parts) is held for confirmation first.
  const handlePartSortRequest = (oldIndex, newIndex) => {
    if (oldIndex === newIndex) return;

    const { parts, crossedSection, movedId } = computeMovedParts(data.parts, oldIndex, newIndex);

    if (crossedSection) {
      const changes = buildRenumberDiff(data.parts, parts, movedId);
      // The moved part is always in `changes` for context, so only prompt when something is
      // actually renumbered to a different number.
      const hasRenumber = changes.some((change) => Number(change.from) !== Number(change.to));

      if (hasRenumber) {
        setPendingMove({ parts, changes, sectionName: getSectionNameForId(parts, movedId) });
        return;
      }
    }

    setData({ ...data, parts });
  };

  const handleConfirmMove = () => {
    if (pendingMove) setData({ ...data, parts: pendingMove.parts });
    setPendingMove(null);
  };

  // Cancelling discards the proposed parts and remounts the list (via listRefreshKey) so the
  // row SortableJS already dragged into place snaps back to its original position.
  const handleCancelMove = () => {
    setPendingMove(null);
    setListRefreshKey((key) => key + 1);
  };

  /**
   * The half-step buttons move the chords in the textarea. The part's chart holds chords the
   * textarea doesn't, so they are moved here by the same interval — read against the same key the
   * buttons use, which is the part's own where it declares one.
   */
  const handleTransposeChart = (direction) => {
    setPendingChart((chart) =>
      chart ? mapChartDataChords(chart, (text) => transposeChordText(text, editingPartKey, direction)) : chart,
    );
  };

  /**
   * Keep the chart as it is, or go and change it.
   *
   * "Keep it" saves the edit and leaves the chart alone. "Update chart" deliberately saves nothing
   * yet — it opens the builder on the chart with the edit still in the editor, so the words and the
   * chart go onto the part together when Update Part is pressed again. Dismissing the prompt leaves
   * the edit unsaved, the way the repeat prompt below does.
   */
  const handleChartUpdateAnswer = (update) => {
    if (!pendingChartUpdate) return;

    const { name, lyrics } = pendingChartUpdate;

    setPendingChartUpdate(null);

    // Answered here only by keeping the chart. On the other branch the builder answers it, by
    // being saved — backing out of it leaves the question open, so Update Part asks again rather
    // than saving against a chart the admin went to change and didn't.
    if (update) setIsChordBarsModalOpen(true);
    else {
      chartWarningAnswered.current = true;
      addPart(name, lyrics);
    }
  };

  // Typed bar lines replace the part's chart, or the edit waits in the editor for them to be changed
  const handleLyricsChartAnswer = () => {
    if (!pendingLyricsChart) return;

    const { name, lyrics } = pendingLyricsChart;

    setPendingLyricsChart(null);
    lyricsChartAnswered.current = true;
    addPart(name, lyrics);
  };

  // Both answers save the edit; they differ only in whether the duplicates take the new repeat.
  // Dismissing the prompt instead leaves the edit unsaved, still in the editor.
  const handleRepeatSpreadAnswer = (spreadRepeat) => {
    if (!pendingRepeatSpread) return;

    const { name, lyrics } = pendingRepeatSpread;

    setPendingRepeatSpread(null);
    addPart(name, lyrics, spreadRepeat);
  };

  /**
   * Declares the key from this part onward, or clears the declaration so it inherits again.
   *
   * Nothing else is touched, and nothing is asked. The key carries forward on its own — see
   * getPartKeys — so setting it here is the whole of saying "the song is in this key from here",
   * and a later change is a declaration on the later part. Writing it onto the parts below would
   * be storing the same answer a dozen times and then having to keep the copies in step.
   *
   * Committed to the part straight away, like assigning instruments or attaching a note: it is a
   * fact about the part rather than a change to the text in the editor, so it survives whether the
   * edit is saved or cancelled.
   *
   * A duplicate never declares one — the select is disabled on one — so it takes the key in force
   * wherever it happens to sit, like any part that declares nothing.
   */
  const handlePartKeyChange = (key) => {
    setPendingPartKey(key);

    if (!editPartId) return;

    setData({
      ...data,
      parts: data.parts.map((part) => {
        if (part.id !== editPartId) return part;

        const updated = { ...part };

        // Absent rather than empty: the backend's part schema takes a key of 1-7 characters or
        // none at all, and none is what inheriting means
        if (key) updated.key = key;
        else delete updated.key;

        return updated;
      }),
    });
  };

  // The tempo this part plays at, committed straight away like the key. It covers this part only.
  const handlePartBpmChange = (bpm) => {
    setPendingPartBpm(bpm);

    if (!editPartId) return;

    setData({
      ...data,
      parts: data.parts.map((part) => {
        if (part.id !== editPartId) return part;

        const updated = { ...part };
        if (bpm) updated.bpm = Number(bpm);
        else delete updated.bpm;

        return updated;
      }),
    });
  };

  const handlePartChange = ({ currentTarget: input }) => {
    const newData = { ...data };
    // set input to state manually
    newData[input.name] = input.value;

    // Recalculate part number for the newly selected part
    if (input.name === 'currentPart') {
      newData.partNumber = getNextPartNumber(newData.parts, input.value);
    }

    setData(newData);
    setEditPartId(editPartId);
  };

  const handlePartDelete = (part, partIndex) => {
    const newData = { ...data };
    const prevData = _.cloneDeep(data);
    const { parts, currentPart } = newData;

    // Delete part
    parts.splice(partIndex, 1);

    // Check if the original part has more duplicates
    if (part.isDuplicate) {
      const duplicates = parts.filter((p) => p.duplicateId === part.duplicateId);

      if (duplicates.length === 0) {
        const index = parts.findIndex((p) => p.id === part.duplicateId);

        parts[index].hasDuplicates = false;
      }
    }

    // Update partNumber
    if (part.name === currentPart) {
      newData.partNumber = getNextPartNumber(newData.parts, currentPart);
    }

    // A note attached to the deleted part loses that host. Dropping the dangling id keeps the
    // note from silently counting a part that no longer exists — with no hosts left it goes back
    // to standing on its own.
    parts.forEach((p) => {
      if (isAttachedToPart(p, part.id)) {
        p.attachedToPartIds = getAttachedToPartIds(p).filter((id) => id !== part.id);
      }
    });

    // sort note parts
    sortNoteParts(parts);

    newData.parts = parts;

    setData(newData);

    showUndoToast(part, 'deleted', prevData);
  };

  // Used by the parts list so the row can flash red twice before it actually disappears.
  // The grid modal has no flash UI, so it calls handlePartDelete directly instead.
  const handlePartDeleteWithFlash = (part, partIndex) => {
    setFlashingPart({ id: part.id, type: 'delete' });

    flashTimerRef.current = setTimeout(() => {
      setFlashingPart(null);
      handlePartDelete(part, partIndex);
    }, PART_FLASH_DURATION);
  };

  const handlePartDuplicate = (part, e) => {
    const newData = { ...data };
    const prevData = _.cloneDeep(data);
    const parts = [...newData.parts];

    // Create duplicate
    const duplicate = {
      isDuplicate: true,
      duplicateId: part.id,
      id: Date.now().toString(),
      name: part.name,
      number: part.number,
      lyrics: part.lyrics,
      repeatCount: part.repeatCount,
      ...(part.midi && { midi: part.midi }),
      ...(part.chartData && { chartData: part.chartData }),
      ...(part.chartInstrumentId && { chartInstrumentId: part.chartInstrumentId }),
      ...(part.chartRhythm && { chartRhythm: part.chartRhythm }),
      // Instruments are inherited as a starting point, but the duplicate owns them from here
      // on — changing them doesn't break the lyrics/chords link to the original.
      // Assigned members are deliberately not copied — that's a per-performance decision.
      assignedInstruments: part.assignedInstruments ? [...part.assignedInstruments] : [],
      soloInstruments: part.soloInstruments ? [...part.soloInstruments] : [],
    };

    // Place the duplicate: at the end of its section ("Duplicate to Section End"),
    // at the very end if CMD is pressed or "Duplicate to End" was used, otherwise
    // immediately after the original part.
    if (e && e.atSectionEnd) {
      parts.splice(getSectionEndIndex(parts, part.id), 0, duplicate);
    } else if (e && (e.metaKey || e.atEnd)) {
      parts.push(duplicate);
    } else {
      const currentPartIndex = parts.findIndex((p) => p.id === part.id);

      parts.splice(currentPartIndex + 1, 0, duplicate);
    }

    // Mark original part
    const index = parts.findIndex((p) => p.id === part.id);

    parts[index].hasDuplicates = true;

    newData.parts = parts;
    setData(newData);

    // Flash the new duplicate twice so the user notices where it landed
    setFlashingPart({ id: duplicate.id, type: 'duplicate' });
    flashTimerRef.current = setTimeout(() => {
      setFlashingPart((prev) => (prev?.id === duplicate.id ? null : prev));
    }, PART_FLASH_DURATION);

    showUndoToast(part, 'duplicated', prevData);
  };

  const showUndo = (label, prevData) => {
    const resetState = () => {
      setData(prevData);
      toast.info(t('songForm.undone'), { hideProgressBar: true });
    };

    const undo = () => {
      return (
        <div className="undo-toast">
          <div>{label}</div>
          <div className="undo-toast__undo" onClick={resetState}>
            {t('songForm.undo')}
          </div>
        </div>
      );
    };

    toast.info(undo);
  };

  const showUndoToast = (part, message, prevData) =>
    showUndo(t(`songForm.undoMessages.${message}`, { part: `${partName(part.name)} ${part.number}` }), prevData);

  const handlePartEdit = (part) => {
    const newData = { ...data };
    newData.currentPart = part.name;
    newData.partNumber = part.number;
    newData.currentLyrics = part.lyrics;
    newData.currentRepeatCount = part.repeatCount || 1;
    setChartInstrumentId(part.chartInstrumentId || null);
    setChartRhythm(part.chartRhythm || null);
    setPendingChart(hasChartData(part.chartData) ? part.chartData : null);
    chartWarningAnswered.current = false;
    lyricsChartAnswered.current = false;
    setPendingPartKey(part.key || '');
    setPendingPartBpm(getPartBpm(part, data.bpm) || '');
    setEditPartId(part.id);
    setData(newData);
    clearLyricsHistory();
    showTab('lyrics', { fromTop: true });
  };

  const handleCancelEdit = () => {
    const newData = { ...data };

    newData.currentPart = 'Intro';
    newData.currentLyrics = '';
    newData.currentRepeatCount = 1;

    // Update part number for current part name
    newData.partNumber = getNextPartNumber(newData.parts, newData.currentPart);
    setData(newData);
    setEditPartId(null);
    setPendingMidi(null);
    setChartInstrumentId(null);
    setChartRhythm(null);
    setPendingChart(null);
    setPendingPartKey('');
    setPendingPartBpm('');
    chartWarningAnswered.current = false;
    lyricsChartAnswered.current = false;
    setPendingAssignedUsers([]);
    setPendingInstruments([]);
    setPendingSoloInstruments([]);
    clearLyricsHistory();
  };

  // The same actions the "Aa" menu in LyricsActions offers, so formatting stays one keystroke
  // now that the buttons live behind a trigger
  const FORMAT_SHORTCUTS = {
    b: handleBoldButton,
    i: handleItalicButton,
    u: handleUnderlineButton,
  };

  const handleKeyDown = (e) => {
    if (!e.metaKey && !e.ctrlKey) return;

    const key = e.key.toLowerCase();
    const historyStep = getHistoryShortcut(e);

    if (historyStep) {
      e.preventDefault();

      if (historyStep === 'redo') {
        redoLyrics();
      } else {
        undoLyrics();
      }
      return;
    }

    const formatAction = !e.shiftKey && !e.altKey && FORMAT_SHORTCUTS[key];

    if (formatAction) {
      e.preventDefault(); // Ctrl+U is view-source on Windows browsers
      formatAction(handleInputToState);
    }
  };

  // Escape cancels the part edit no matter where focus is — not just while the lyrics
  // textarea itself is focused — unless a modal is open over the form.
  useEffect(() => {
    if (!editPartId) return undefined;

    const handleGlobalEscape = (e) => {
      if (!escapeCancelsEdit(e)) return;
      e.preventDefault();
      handleCancelEdit();
    };

    document.addEventListener('keydown', handleGlobalEscape);
    return () => document.removeEventListener('keydown', handleGlobalEscape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPartId]);

  const handlePartUpdate = (partId, newLyrics) => {
    const newData = { ...data };
    const prevData = _.cloneDeep(data);
    const { parts } = newData;

    // Find and update the part
    const partIndex = parts.findIndex((p) => p.id === partId);

    if (partIndex === -1) return;

    const part = parts[partIndex];
    const oldLyrics = part.lyrics;

    // Update the part
    parts[partIndex] = { ...part, lyrics: newLyrics };

    // Apply changes to duplicates, if any
    if (part.hasDuplicates) {
      parts.forEach((p, index) => {
        if (p.isDuplicate && p.duplicateId === part.id) {
          parts[index] = { ...p, lyrics: newLyrics };
        }
      });
    }

    // If this is a duplicate part and lyrics changed, make it independent
    if (part.isDuplicate) {
      const originalPart = parts.find((p) => p.id === part.duplicateId);

      if (originalPart && newLyrics !== originalPart.lyrics) {
        const updatedPart = { ...parts[partIndex] };

        delete updatedPart.isDuplicate;
        delete updatedPart.duplicateId;
        parts[partIndex] = updatedPart;

        // Check if the original part has more duplicates
        const duplicates = parts.filter((p) => p.duplicateId === originalPart.id);

        if (duplicates.length === 0) {
          const originalIndex = parts.findIndex((p) => p.id === originalPart.id);
          parts[originalIndex] = { ...originalPart, hasDuplicates: false };
        }
      }
    }

    newData.parts = parts;
    setData(newData);

    // Show undo toast
    showUndoToast({ ...part, lyrics: oldLyrics }, 'updated', prevData);
  };

  // Every change the intensity panel makes goes through here, so it can be undone. The update is
  // run on this render's data rather than as a setter, to know whether it changed anything.
  const arrangeEdit = (update) => {
    const next = update(data);

    if (!_.isEqual(getArrangement(next), getArrangement(data))) {
      setArrangeHistory((history) => pushHistory(history, getArrangement(data)));
    }

    setData(next);
  };

  const stepArrange = (step) => {
    const taken = step(arrangeHistory, getArrangement(data));

    if (!taken) return;

    setArrangeHistory(taken.history);
    setData((prev) => applyArrangement(prev, taken.snapshot));
  };

  // Sets intensity fields on every part picked. A value that is the default is stored as nothing,
  // so a song left alone saves as it always did.
  // `layers` is players by id, each 'on', 'off', or null for as the level has it. `instrument` and
  // `rhythm` are the part's own, null to follow the song's.
  const handleArrange = ({ intensity, fill, drop, pad, passes, layers, instrument, rhythm }) => {
    arrangeEdit((prev) => ({
      ...prev,
      parts: prev.parts.map((part) => {
        if (!arrangeIds.includes(part.id)) return part;

        const next = { ...part };

        if (intensity !== undefined) {
          next.chartIntensity = intensity;
          // A new level for the part starts its passes over from it
          delete next.chartIntensityPasses;
        }
        // A build saved before the passes took its place, taken over by whatever level is set now
        if (intensity !== undefined || passes !== undefined) delete next.chartBuild;
        if (fill !== undefined) next.chartFill = fill;
        if (drop !== undefined) next.chartDrop = drop;
        if (pad !== undefined) next.chartPad = pad;
        if (passes !== undefined) next.chartIntensityPasses = passes;
        if (layers !== undefined) next.chartLayers = _.omitBy({ ...next.chartLayers, ...layers }, _.isNil);
        if (instrument) next.chartInstrumentId = instrument;
        else if (instrument === null) delete next.chartInstrumentId;
        if (rhythm) next.chartRhythm = rhythm;
        else if (rhythm === null) delete next.chartRhythm;

        if (next.chartIntensity === 3) delete next.chartIntensity;
        if (next.chartFill !== false) delete next.chartFill;
        if (!next.chartDrop) delete next.chartDrop;
        if (!next.chartPad) delete next.chartPad;
        if (!next.chartIntensityPasses?.some(Boolean)) delete next.chartIntensityPasses;
        if (_.isEmpty(next.chartLayers)) delete next.chartLayers;

        return next;
      }),
    }));
  };

  // These parts back to how they played before they were arranged
  const handleArrangeReset = (ids) =>
    arrangeEdit((prev) => ({
      ...prev,
      parts: prev.parts.map((part) =>
        ids.includes(part.id)
          ? _.omit(part, [
              'chartIntensity',
              'chartIntensityPasses',
              'chartBuild',
              'chartFill',
              'chartDrop',
              'chartPad',
              'chartLayers',
            ])
          : part,
      ),
    }));

  // Every part given the song's usual shape, its fill left as it was. A drop and the pad are only
  // ever added, so one set by hand stays.
  const handleArrangeSuggest = () => {
    const suggested = suggestIntensities(data.parts, canArrange);

    arrangeEdit((prev) => ({
      ...prev,
      parts: prev.parts.map((part) => {
        const level = suggested.get(part.id);

        if (!level) return part;

        const next = _.omit(part, ['chartIntensity', 'chartIntensityPasses', 'chartBuild']);

        if (level.intensity !== 3) next.chartIntensity = level.intensity;
        if (level.passes) next.chartIntensityPasses = level.passes;
        if (level.drop) next.chartDrop = true;
        if (level.pad) next.chartPad = level.pad;

        return next;
      }),
    }));
  };

  const toggleArrangeId = (id) =>
    setArrangeIds((ids) => (ids.includes(id) ? ids.filter((other) => other !== id) : [...ids, id]));

  // Opens the intensity panel with just this part picked. The panel can't sit beside the editor,
  // so a part open in it is closed first.
  const openInIntensity = (part) => {
    if (editPartId) handleCancelEdit();

    setArranging(true);
    setArrangeIds([part.id]);
    // The panel sits over the parts list, which on a phone is its own tab
    showTab('parts');
  };

  // Whether the part open in the editor differs from what is saved on it
  const editHasChanges = () => {
    const part = data.parts.find((p) => p.id === editPartId);

    if (!part) return false;

    return (
      data.currentLyrics !== part.lyrics ||
      data.currentPart !== part.name ||
      Number(data.partNumber) !== Number(part.number) ||
      Number(data.currentRepeatCount) !== (part.repeatCount || 1) ||
      pendingPartKey !== (part.key || '') ||
      String(pendingPartBpm) !== String(getPartBpm(part, data.bpm) || '') ||
      (chartInstrumentId || null) !== (part.chartInstrumentId || null) ||
      (chartRhythm || null) !== (part.chartRhythm || null) ||
      !_.isEqual(pendingChart, hasChartData(part.chartData) ? part.chartData : null)
    );
  };

  const handleOpenEditInIntensity = () => {
    const part = data.parts.find((p) => p.id === editPartId);

    if (editHasChanges()) setPendingIntensityPart(part);
    else openInIntensity(part);
  };

  const renderAddButtonText = () => {
    return editPartId ? t('songForm.updatePart') : t('songForm.addPart');
  };

  const handleOpenGridModal = () => {
    setIsGridModalOpen(true);
  };

  const handleCloseGridModal = () => {
    setIsGridModalOpen(false);
  };

  const handleOpenAssignModal = () => {
    if (editPartId) {
      const part = data.parts.find((p) => p.id === editPartId);

      setAssigningPart(part ?? null);
    } else {
      setAssigningPart({
        id: null,
        assignedUsers: pendingAssignedUsers,
        assignedInstruments: pendingInstruments,
        soloInstruments: pendingSoloInstruments,
      });
    }

    setIsAssignModalOpen(true);
  };

  const handleAssignPart = (partId, { assignedUsers, assignedInstruments, soloInstruments }) => {
    if (partId === null) {
      setPendingAssignedUsers(assignedUsers);
      setPendingInstruments(assignedInstruments);
      setPendingSoloInstruments(soloInstruments);
      return;
    }

    const partExists = data.parts.some((p) => p.id === partId);
    if (!partExists) return; // id not found

    // Filter out any users no longer on the team (e.g. removed after assignment).
    // This ensures stale assignments are cleaned up when the part is updated.
    const validUserIds = new Set(teamUsers.map((u) => u._id));
    const filteredAssignedUsers = assignedUsers.filter((u) => u?._id && validUserIds.has(u._id));
    // Same idea for instruments — one dropped from the curated list shouldn't linger on the part
    const validInstruments = new Set(instruments);
    const filteredInstruments = assignedInstruments.filter((i) => validInstruments.has(i));
    const filteredSoloInstruments = soloInstruments.filter((i) => filteredInstruments.includes(i));
    const newParts = data.parts.map((p) =>
      p.id === partId
        ? {
            ...p,
            assignedUsers: filteredAssignedUsers,
            assignedInstruments: filteredInstruments,
            soloInstruments: filteredSoloInstruments,
          }
        : p,
    );

    setData({ ...data, parts: newParts });
  };

  // Attaching a note to a part is committed straight away (like assigning instruments), so it
  // survives whether the edit is saved or cancelled. It targets this exact part — a duplicate of
  // the host is a separate part and doesn't inherit the note.
  const handleToggleAttach = (noteId, partId) => {
    if (!noteId || !partId) return;

    const newParts = data.parts.map((p) => {
      if (p.id !== noteId) return p;

      const attachedToPartIds = getAttachedToPartIds(p);

      return {
        ...p,
        attachedToPartIds: attachedToPartIds.includes(partId)
          ? attachedToPartIds.filter((id) => id !== partId)
          : [...attachedToPartIds, partId],
      };
    });

    setData({ ...data, parts: newParts });
  };

  const handleOpenMidiModal = () => {
    if (editPartId) {
      const part = data.parts.find((p) => p.id === editPartId);

      setMidiPart(part ?? null);
    } else {
      // No part exists yet — hold the MIDI until Add Part creates one
      setMidiPart({ id: null, midi: pendingMidi });
    }

    setIsMidiModalOpen(true);
  };

  const handleSetPartMidi = (partId, midi) => {
    if (partId === null) {
      setPendingMidi(midi);
      return;
    }

    const partExists = data.parts.some((p) => p.id === partId);
    if (!partExists) return; // id not found

    const newParts = data.parts.map((p) => {
      if (p.id !== partId) return p;

      const { midi: previousMidi, ...rest } = p;

      // Removing the MIDI drops the property rather than storing an empty one
      return midi ? { ...rest, midi } : rest;
    });

    setData({ ...data, parts: newParts });
  };

  const handleClose = () => {
    // Navigate back to where the user came from
    const { state } = props.location;
    if (state && state.from) {
      const returnUrl = new URL(state.from, window.location.origin);
      props.history.push(returnUrl.pathname + returnUrl.search + returnUrl.hash);
    } else {
      props.history.push('/songs');
    }
    scrollTop();
  };

  const handleIsPublic = () => {
    const newData = { ...data };
    newData.isPublic = !newData.isPublic;
    setData(newData);
  };

  const handleIsHidden = () => {
    const newData = { ...data };
    newData.isHidden = !newData.isHidden;

    // If setting isHidden to true, automatically set isPublic to false
    if (newData.isHidden) {
      newData.isPublic = false;
    }

    setData(newData);
  };

  const handleIsDraft = () => {
    const newData = { ...data };
    newData.isDraft = !newData.isDraft;

    // If setting isDraft to true or if it's already hidden, ensure isPublic is false
    if (newData.isDraft || newData.isHidden) {
      newData.isPublic = false;
    }

    setData(newData);
  };

  const handleIsNew = () => {
    const newData = { ...data };
    newData.isNewSong = !newData.isNewSong;
    setData(newData);
  };

  /**
   * The song's own key. Only name and value are ever read off the element, so the change can be
   * replayed from a plain object once the admin has confirmed it.
   */
  const handleKeyChange = (e) => {
    const { name, value } = e.currentTarget;

    if (value === data.key) {
      handleChange(e);
      return;
    }

    guardMidiKeyChange(() => handleChange({ currentTarget: { name, value } }));
  };

  const handleTransposeToChange = (e) => {
    const selectedKey = e.currentTarget.value;

    guardMidiKeyChange(() => applyTransposeToChange(selectedKey));
  };

  const applyTransposeToChange = (selectedKey) => {
    const newData = { ...data };

    if (!selectedKey || !data.key) {
      // "Transpose" option selected or no original key set
      newData.transposeBy = '';
    } else if (selectedKey === data.key) {
      // Current key selected - no transposition needed
      newData.transposeBy = '';
    } else {
      // Calculate semitones from original key to selected key
      const semitones = calculateSemitones(data.key, selectedKey);
      newData.transposeBy = semitones;
    }

    setData(newData);
  };

  // Whether anything attached to this song would move if the key or the transpose changed.
  // pendingMidi counts too: a clip picked for a part that hasn't been added yet is just as
  // anchored as a saved one, and is the case where a warning is easiest to act on.
  const songHasMidi = (data.parts?.some(hasMidi) ?? false) || !!pendingMidi;

  /**
   * Run a key or transpose edit past the admin first when it would re-pitch an attached clip.
   *
   * The change is held rather than applied and undone: nothing reaches the form until Continue,
   * so Cancel is simply doing nothing, and there is no half-applied state to unwind.
   */
  const guardMidiKeyChange = (apply) => {
    if (!songHasMidi) {
      apply();
      return;
    }

    // Wrapped rather than stored bare: a function passed to a setter is taken as an updater
    setPendingKeyWarning({ apply });
  };

  const handleConfirmKeyWarning = () => {
    pendingKeyWarning?.apply();
    setPendingKeyWarning(null);
  };

  // Get the target key for the transpose dropdown based on transposeBy value
  const getTransposeDropdownValue = () => {
    if (!data.transposeBy || !data.key) return '';

    const semitones = typeof data.transposeBy === 'string' ? parseInt(data.transposeBy) : data.transposeBy;

    if (isNaN(semitones) || semitones === 0) return '';

    return getKeyAfterTranspose(data.key, semitones);
  };

  // What the song actually sounds in once its own transpose is applied. The MIDI modal's key row
  // and its preview both follow this, so an admin hears a part the way a viewer will rather than
  // in a key nothing is ever played in. Resolved here rather than in View, which only displays
  // what it is handed. Falls back to the song's own key, which is what no transpose means.
  const soundingKey = getTransposeDropdownValue() || data.key;

  // The key of the part in the editor right now — its own if it has been given one, the song's
  // otherwise. Everything that reads a key while a part is being written goes through this rather
  // than through data.key: the chords being suggested, the scale on the piano, the half-step
  // transpose buttons, and the key a MIDI clip is anchored to. A bridge in Am offered the song's
  // seven chords in Dm is offering the wrong seven.
  const editingPartKey = pendingPartKey || data.key;

  // The key the bar chart builds its chords from — unlike editingPartKey, this includes a key
  // inherited from an earlier part. A new part lands at the end, so it takes the song's end key.
  const chartKey =
    pendingPartKey ||
    (editPartId ? getPartKeys(data.parts, data.key)[editPartId]?.key : getSongEndKey(data.parts, data.key)) ||
    data.key;

  // Its counterpart to soundingKey: where that part actually lands once the song's own transpose
  // is applied on top, which is what the MIDI preview has to play in.
  const editingSoundingKey = pendingPartKey
    ? getKeyAfterTranspose(pendingPartKey, getKeyRootDistance(data.key, soundingKey))
    : soundingKey;

  const partKeyOptions = getPartKeyOptions(data.key);
  // Stored by their English names, like the category pills; only the label is translated
  const categoryOptions = categories.map((category) => ({
    ...category,
    name: t(`categories.${category.name.toLowerCase()}`, { defaultValue: category.name }),
  }));

  // What the chart builder, its preview and the repeat timer play this part at
  const editingPartBpm = pendingPartBpm || data.bpm;

  // The chart defaults have no chart of their own to play, so their sheets play a progression in the key
  const songInstrumentId = data.chartInstrumentId || DEFAULT_INSTRUMENT_ID;
  const chartPreview = useKeyProgressionPreview({
    key: data.key,
    bpm: data.bpm,
    timeSignature: data.timeSignature,
    category: categories.find((category) => category._id === data.categoryId)?.name,
    instrumentId: songInstrumentId,
    rhythm: toRhythmId(data.chartRhythm || BLOCK_CHORDS, getBeatsPerBar(data.timeSignature)),
    isActive: isChartSheetOpen,
  });
  const chartPlayer = data.key ? chartPreview : undefined;
  // Space plays and stops it while the Rhythm or Instrument sheet is open
  useSpacePlay(chartPreview, isChartSheetOpen);

  // A duplicate takes the key in force where it sits and cannot declare one, so the select says so
  // rather than accepting a value that would be dropped
  const editingPartIsDuplicate = !!data.parts.find((p) => p.id === editPartId)?.isDuplicate;

  const renderTransposeSelect = () => {
    return (
      <div className="mg-form-group">
        <select
          name="transposeToKey"
          id="transposeToKey"
          className="mg-custom-select transposeToKey"
          value={getTransposeDropdownValue()}
          onChange={handleTransposeToChange}
          disabled={!data.key}
        >
          <option value="">{t('songForm.noTranspose')}</option>
          {transposeKeys.map((option) => (
            <option key={option._id} value={option._id} disabled={option._id === data.key}>
              {option.name}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const handleThemeChange = (newThemes) => {
    setData({ ...data, themes: newThemes });
  };

  const handleSuggestedThemeClick = (theme) => {
    // Add the theme to the current themes if not already present (always lowercase)
    const currentThemes = data.themes || [];
    const normalizedTheme = theme.toLowerCase();
    if (!currentThemes.includes(normalizedTheme)) {
      const newThemes = [...currentThemes, normalizedTheme];
      setData({ ...data, themes: newThemes });
    }
  };

  const pushLyricsHistory = (previousValue) => {
    lyricsTypedAt.current = null;
    lyricsUndoStack.current.push(previousValue);

    if (lyricsUndoStack.current.length > MAX_LYRICS_HISTORY) {
      lyricsUndoStack.current.shift();
    }

    lyricsRedoStack.current = [];
  };

  const clearLyricsHistory = () => {
    lyricsTypedAt.current = null;
    lyricsUndoStack.current = [];
    lyricsRedoStack.current = [];
  };

  const undoLyrics = () => {
    if (lyricsUndoStack.current.length === 0) return;

    const previousValue = lyricsUndoStack.current.pop();

    lyricsTypedAt.current = null;
    lyricsRedoStack.current.push(data.currentLyrics);
    setData({ ...data, currentLyrics: previousValue });
  };

  const redoLyrics = () => {
    if (lyricsRedoStack.current.length === 0) return;

    const nextValue = lyricsRedoStack.current.pop();

    lyricsTypedAt.current = null;
    lyricsUndoStack.current.push(data.currentLyrics);
    setData({ ...data, currentLyrics: nextValue });
  };

  const handleLyricsChange = (input) => {
    const { name, value } = input.target;

    // A run of typing undoes as one step, rather than a letter at a time
    if (name === 'currentLyrics' && value !== data.currentLyrics) {
      const now = Date.now();
      const run = { lastTypedAt: lyricsTypedAt.current, now, previous: data.currentLyrics, next: value };

      if (!continuesTypingRun(run)) pushLyricsHistory(data.currentLyrics);

      lyricsTypedAt.current = now;
    }

    const newData = { ...data };
    newData[name] = value;
    setData(newData);
  };

  const handleInputToState = (input) => {
    let inputElement = input;

    if ('currentTarget' in input) {
      inputElement = input.currentTarget;
    }

    if (inputElement.name === 'currentLyrics' && inputElement.value !== data.currentLyrics) {
      pushLyricsHistory(data.currentLyrics);
    }

    const newData = { ...data };

    newData[inputElement.name] = inputElement.value;
    setData(newData);
  };

  const handleCopyLyrics = async () => {
    if (!data.currentLyrics) return;

    try {
      await navigator.clipboard.writeText(data.currentLyrics);

      toast.info(t('songForm.lyricsCopied'), { autoClose: 2000, hideProgressBar: true });
    } catch (error) {}
  };

  // Pushed onto the lyrics history like any other edit, so Ctrl+Z inside the textarea brings
  // the whole block back — that is what stands in for a confirmation prompt here.
  const handleClearLyrics = () => {
    if (!data.currentLyrics) return;

    pushLyricsHistory(data.currentLyrics);
    setData({ ...data, currentLyrics: '' });
    document.getElementById('currentLyrics')?.focus();
  };

  const handleInsertRepeatDirective = (directive) => {
    const REPEAT_REGEX = /^\{\d+x,\d+(?:\.\d+)?\}(\r?\n)?/;

    pushLyricsHistory(data.currentLyrics);

    setData((prev) => {
      const existing = prev.currentLyrics;
      const updated = REPEAT_REGEX.test(existing)
        ? existing.replace(REPEAT_REGEX, `${directive}\n`)
        : `${directive}\n${existing}`;

      return { ...prev, currentLyrics: updated };
    });
  };

  /**
   * Keep the builder's chart for the part being written, as chartData.
   *
   * A chart imported from an instrumental's written chord lines replaces them, so they come out of
   * its lyrics. A part made of words keeps its lyrics exactly as they were typed: its chords sit over
   * the words, and the chart beside them.
   */
  const handleInsertChordBars = (chart, { instrumentId, rhythm }, { replaceChords = false } = {}) => {
    if (!chart) return;

    // Committed with the chart rather than as the browser is used, so backing out of the modal
    // leaves the part sounding as it did — the instrument is the chart's, and there is no chart
    // until it is saved
    setChartInstrumentId(instrumentId);
    setChartRhythm(rhythm);
    setPendingChart(chart);
    // The chart now describes the lyrics as they stand, so there is nothing left to warn about
    chartWarningAnswered.current = true;

    if (!replaceChords || canAttachChart(data.currentPart)) return;

    pushLyricsHistory(data.currentLyrics);

    setData((prev) => {
      const imported = findChordLines(prev.currentLyrics);

      if (!imported) return prev;

      const rows = prev.currentLyrics.split(/\r\n|\r|\n/);

      rows.splice(imported.startLine, imported.endLine - imported.startLine + 1);

      return { ...prev, currentLyrics: rows.join('\n').trim() };
    });
  };

  /**
   * Take the chart off the part being written. Held like the chart itself rather than written
   * straight onto the part, so backing out of the editor leaves the part charted as it was.
   */
  const handleRemoveChordBars = () => {
    setPendingChart(null);
    // There is no chart left for the stale-chart prompt to ask about
    chartWarningAnswered.current = true;
  };

  // Saved with the song, like the chart defaults
  const handleEffectsChange = (chartEffects) => setData((prev) => ({ ...prev, chartEffects }));

  // The default instrument, like block chords, is stored as nothing
  const withInstrument = (id) => (prev) => ({ ...prev, chartInstrumentId: id === DEFAULT_INSTRUMENT_ID ? null : id });
  const handleInstrumentChange = (id) => setData(withInstrument(id));

  // Block chords is what a song plays with nothing set, so it is stored as nothing
  const withRhythm = (choice) => (prev) => ({ ...prev, chartRhythm: choice === BLOCK_CHORDS ? null : choice });
  const handleRhythmChange = (choice) => setData(withRhythm(choice));

  const doSubmit = async () => {
    try {
      setSavingSong(true);

      // Clean up data before saving
      const dataToSave = { ...data };

      // An empty transpose has to be sent, not dropped. The API $sets the fields it is handed,
      // so leaving transposeBy out keeps whatever was stored and the old transposed key survives
      // the save. '' is stored as no transpose at all.
      if (!dataToSave.transposeBy) {
        dataToSave.transposeBy = '';
      }

      const response = await songService.saveSong(dataToSave);
      const savedSong = response.data;

      // Update global state if data has been loaded
      if (dataLoaded && savedSong) {
        const songsData = [...songs];
        const existingIndex = songsData.findIndex((s) => s._id === savedSong._id);

        if (existingIndex !== -1) {
          // Update existing song
          songsData[existingIndex] = savedSong;
        } else {
          // Add new song
          songsData.push(savedSong);
        }

        setSongs(songsData);
      }

      // Redirect the user to where they came from
      const { state } = props.location;
      if (state && state.from) {
        // Use URL constructor to properly handle query parameters
        const returnUrl = new URL(state.from, window.location.origin);
        returnUrl.searchParams.set('fromEdit', 'true');

        props.history.push(returnUrl.pathname + returnUrl.search + returnUrl.hash);
      } else {
        // Fallback to songs list if no return URL is provided
        props.history.push('/songs');
      }
      scrollTop(undefined, false);
    } catch (ex) {
      setSavingSong(false);
    }
  };

  // When the YouTube field changes and the Title is still empty, auto-fill the title from
  // the pasted/entered video via the oEmbed endpoint. Uses the functional setData form so it
  // doesn't clobber the youtube value that handleChange sets synchronously.
  const handleYouTubeChange = async (e) => {
    handleChange(e);

    if (data.title.trim()) return;

    const videoId = extractYouTubeVideoId(e.currentTarget.value);
    if (!videoId) return;

    const title = await fetchYouTubeTitle(videoId);
    if (!title) return;

    setData((prev) => (prev.title.trim() ? prev : { ...prev, title: title.slice(0, 60) }));
  };

  const renderYouTubePreview = () => {
    const { youtube } = data;

    if (!youtube || youtube.trim() === '') return null;

    const videoId = extractYouTubeVideoId(youtube);

    if (!videoId) return null;

    return (
      <div className="youtube-preview mg-mb-3">
        <div className="embed-responsive">
          <iframe
            className="embed-responsive-item"
            src={`https://www.youtube.com/embed/${videoId}`}
            title={t('songForm.youtubePreview')}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </div>
    );
  };

  const renderEditPartBadge = () => {
    if (!editPartId) return null;

    const part = data.parts.find((p) => p.id === editPartId);

    if (part?.isDuplicate) {
      return (
        <span className="lyrics-textarea-original-badge" title={t('songForm.duplicatePartBadge')}>
          <Icon icon={<RepeatIcon className="duplicate-icon" />} />
        </span>
      );
    }

    const duplicateCount = part?.hasDuplicates ? data.parts.filter((p) => p.duplicateId === part.id).length : 0;

    return (
      <span className="lyrics-textarea-original-badge" title={t('songForm.originalPartBadge')}>
        <Icon icon={<PageStarIcon />} />
        {duplicateCount > 0 && <span className="lyrics-textarea-duplicate-count">{duplicateCount}</span>}
      </span>
    );
  };

  // The column floating in the lyrics textarea's right gutter: cancel edit and the original or
  // duplicate badge, only while a part is open. The actions on the text sit in LyricsActions.
  const renderLyricsTextAreaActions = () => {
    if (!editPartId) return null;

    return (
      <div className="lyrics-textarea-actions">
        <button
          type="button"
          className="lyrics-textarea-clear-btn"
          title={t('songForm.cancelEdit')}
          onClick={handleCancelEdit}
        >
          <Icon icon={<XmarkCircleIcon />} />
        </button>
        {renderEditPartBadge()}
      </div>
    );
  };

  const renderToggleSwitches = (isPublic, isHidden, isCopy, originalTeam) => {
    if (!isCopy) {
      return (
        <div className="song-options horizontal-menu">
          <ToggleChip
            name="isPublic"
            label={t('songForm.public')}
            checked={isPublic}
            onChange={handleIsPublic}
            disabled={isHidden || data.isDraft}
          />
          <ToggleChip
            name="isDraft"
            label={t('songForm.draft')}
            checked={data.isDraft}
            onChange={handleIsDraft}
            color="var(--draft-color)"
          />
          <ToggleChip
            name="isHidden"
            label={t('songForm.hidden')}
            checked={isHidden}
            onChange={handleIsHidden}
            color="var(--hidden-color)"
          />
          <ToggleChip
            name="isNewSong"
            label={t('songForm.new')}
            checked={data.isNewSong}
            onChange={handleIsNew}
            color="var(--new-song-color)"
          />
        </div>
      );
    } else {
      return (
        <div className="copy-note">
          <span className="team-icon">
            <Icon icon={<PeopleIcon />} />
          </span>
          <small> {t('songForm.publishedBy', { team: originalTeam.name })}</small>
          <br />
          <small>{t('songForm.cantBePublic')}</small>
        </div>
      );
    }
  };

  const editingPart = editPartId ? data.parts.find((p) => p.id === editPartId) : null;

  const editingPartHasAssignments = editPartId
    ? !!(editingPart?.assignedUsers?.length || editingPart?.assignedInstruments?.length)
    : pendingAssignedUsers.length > 0 || pendingInstruments.length > 0;

  // An instrumental is mostly empty, so its editor names who plays it until something is written
  const editingInstruments = getInstrumentLabels(
    editPartId ? editingPart : { assignedInstruments: pendingInstruments },
  );
  const lyricsPlaceholder =
    isInstrumentalPart(data.currentPart) && editingInstruments.length > 0 ? editingInstruments.join(' + ') : undefined;

  const editingPartHasMidi = editPartId ? hasMidi(data.parts.find((p) => p.id === editPartId)) : !!pendingMidi;

  // Whether the part being written is made of words, which its chart sits beside
  const isEditingAttachedChart = canAttachChart(data.currentPart);
  const isEditingChartPart = canChartPart(data.currentPart);
  // The Parts header's tools all work on the parts, so they wait for the first one
  const hasParts = data.parts.length > 0;
  const canSavePart = hasPartContent(data.currentLyrics, data.currentPart, hasChartData(pendingChart));

  // The chart builder plays the part's MIDI along with it, in the key the chart is written in
  const editingMidi = editPartId ? editingPart?.midi : pendingMidi;
  const chartMelody = editingPartHasMidi
    ? {
        midiJson: editingMidi,
        instrumentId: getPartInstrumentId(editingMidi),
        semitones: getMidiTranspositionToKey(editingMidi, editingPartKey, loadedSongKey),
      }
    : null;

  // A bar chart is written in columns — "| Dm | Em C | B |" only lines up in a monospaced font, and
  // the same goes for the chords sat over the beats they fall on. A bare search for the character
  // rather than a parse of the line on purpose: the font has to settle as soon as the first bar is
  // typed and stay put, where a stricter test would flip back and forth over a half-written line.
  const writingBarChart = (data.currentLyrics || '').includes('|');

  const getMenuTitle = () => {
    const title = data.title && data.title.trim() ? data.title : '';

    if (!title || isNewSong || !timesPlayed) return title;

    return t('songForm.plays', { title, count: timesPlayed, plays: formatNumber(timesPlayed) });
  };

  return (
    <>
      <PageBar
        back={{ close: true, onClick: handleClose }}
        title={getMenuTitle() || (isNewSong ? t('songForm.newSong') : '')}
        actions={[
          {
            label: t('songForm.saveSong'),
            icon: <SaveIcon />,
            form: 'song-form',
            disabled: loadingSong || !!validationErrors || !!editPartId,
            loading: savingSong,
          },
        ]}
        sticky
      />
      {loadingSong ? (
        <div className="mg-container">
          <NoContent loading={loadingSong} />
        </div>
      ) : (
        <div className="mg-container">
          <form
            id="song-form"
            onSubmit={(e) => handleSubmit(e, doSubmit)}
            onTouchStart={handleSwipeStart}
            onTouchEnd={handleSwipeEnd}
            className="song-form"
          >
            {/* On a phone the three columns become tabs */}
            <div className="mobile-tabs" role="tablist">
              {[
                ['info', t('songForm.infoTab')],
                ['lyrics', t('songForm.lyricsTab')],
                ['parts', t('songForm.parts')],
              ].map(([tab, label]) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={mobileTab === tab}
                  className={cx('mobile-tab', { active: mobileTab === tab })}
                  onClick={() => showTab(tab)}
                >
                  {label}
                  {tab === 'parts' && <span className="tab-count">{data.parts.length}</span>}
                  {tabAttention[tab] && <span className="tab-dot" />}
                </button>
              ))}
            </div>

            {/* Toggle switches at the top, before columns; on a phone, atop the Info tab */}
            <div className={cx('song-form__options-bar', { 'tab-hidden': mobileTab !== 'info' })}>
              {renderToggleSwitches(data.isPublic, data.isHidden, data.isCopy, data.originalTeam)}
            </div>

            <div className="mg-row">
              <div className={cx('mg-col-lg-4', { 'tab-hidden': mobileTab !== 'info' })}>
                <p className="section-header">{t('songForm.songInfo')}</p>
                {showCounter && <SongCounter count={songCount} onClick={() => setShowUpgradeModal(true)} />}
                <div className="song-info-details">
                  {renderInput('title', t('songForm.title'), 'text', data, errors, handleChange, { clearable: true })}
                  <ArtistInput
                    name="artist"
                    value={data.artist}
                    onChange={handleChange}
                    error={errors.artist}
                    artists={artists}
                  />
                  <div className="song-form__input-group">
                    {renderSelect('key', t('view.key'), keys, data, errors, handleKeyChange, true)}
                    {renderTransposeSelect()}
                  </div>
                  <div className="song-form__input-group">
                    {renderInput('bpm', 'BPM', 'number', data, errors, handleChange)}
                    {renderSelect(
                      'timeSignature',
                      t('view.timeSignature'),
                      timeSignaturesData,
                      data,
                      errors,
                      handleChange,
                      true,
                    )}
                  </div>
                  {/* How the song's chord charts play, unless a part says otherwise */}
                  <div className="chart-defaults">
                    <InstrumentRow
                      instrumentId={songInstrumentId}
                      onSelect={handleInstrumentChange}
                      defaultId={DEFAULT_INSTRUMENT_ID}
                      recallLast={false}
                      player={chartPlayer}
                      browser={{
                        downloadedIds: chartPreview.samples.downloadedIds,
                        playingId: chartPreview.isPlaying ? songInstrumentId : undefined,
                        loadingId:
                          chartPreview.isLoading || chartPreview.samples.status === 'loading'
                            ? songInstrumentId
                            : undefined,
                        progress: { loaded: chartPreview.samples.loaded, total: chartPreview.samples.total },
                      }}
                      onOpenChange={setIsChartSheetOpen}
                    />
                    <RhythmStylePicker
                      rhythm={{
                        choice: data.chartRhythm || BLOCK_CHORDS,
                        onChoose: handleRhythmChange,
                        timeSignature: data.timeSignature,
                      }}
                      defaultChoice={BLOCK_CHORDS}
                      player={chartPlayer}
                      onOpenChange={setIsChartSheetOpen}
                    />
                  </div>
                  {renderSelect('categoryId', t('songForm.categories'), categoryOptions, data, errors, handleChange)}
                  <TagInput
                    name="themes"
                    value={data.themes || []}
                    onChange={handleThemeChange}
                    placeholder={t('songForm.themeKeywords')}
                    maxTags={10}
                    suggestions={allThemes}
                    error={errors.themes}
                  />
                  {renderInput('youtube', 'YouTube', 'text', data, errors, handleYouTubeChange, { clearable: true })}
                  {renderYouTubePreview()}
                </div>
              </div>

              <div className={cx('mg-col-lg-5', { 'tab-hidden': mobileTab !== 'lyrics' })}>
                <p className="section-header">{t('songForm.lyricsAndChords')}</p>
                <div className="lyrics-details">
                  <div className="mg-form-row song-form__lyrics-row">
                    <div className="mg-col-6">
                      {renderPartTypeSelect('currentPart', songPartsData, data, errors, handlePartChange)}
                    </div>
                    <div className="mg-col-2">
                      {renderInputNumber(
                        'partNumber',
                        data,
                        errors,
                        handleInputToState,
                        data.currentPart === 'Section',
                      )}
                    </div>
                    <div className="mg-col-4">
                      <button
                        onClick={handleAddPart}
                        type="button"
                        disabled={!canSavePart}
                        className="mg-btn mg-btn--primary max-width song-form__add-part-btn"
                      >
                        <span className="btn__text">{renderAddButtonText()}</span>
                      </button>
                    </div>
                    <LyricsActions
                      setInputToState={handleInputToState}
                      currentKey={editingPartKey}
                      partKey={pendingPartKey}
                      partKeyOptions={partKeyOptions}
                      partKeyInForce={chartKey}
                      onPartKeyChange={handlePartKeyChange}
                      partBpm={pendingPartBpm}
                      songBpm={data.bpm}
                      onPartBpmChange={handlePartBpmChange}
                      isDuplicatePart={editingPartIsDuplicate}
                      bpm={editingPartBpm}
                      onOpenTimerModal={() => setIsTimerModalOpen(true)}
                      editPartId={editPartId}
                      currentLyrics={data.currentLyrics}
                      onOpenAssignModal={handleOpenAssignModal}
                      editingPartHasAssignments={editingPartHasAssignments}
                      onOpenMidiModal={handleOpenMidiModal}
                      editingPartHasMidi={editingPartHasMidi}
                      repeatCount={data.currentRepeatCount}
                      onRepeatCountChange={(value) => setData({ ...data, currentRepeatCount: value })}
                      currentPart={data.currentPart}
                      onOpenChordBarsModal={() => setIsChordBarsModalOpen(true)}
                      editingPartHasChart={isEditingChartPart && !!pendingChart}
                      onTransposeChart={isEditingChartPart ? handleTransposeChart : undefined}
                      onOpenInIntensity={editingPart && canArrange(editingPart) ? handleOpenEditInIntensity : undefined}
                      onCopyLyrics={handleCopyLyrics}
                      onClearLyrics={handleClearLyrics}
                    />
                  </div>
                  <ChordSuggestions currentKey={editingPartKey} setInputToState={handleInputToState} />
                  <div className="mg-form-group lyrics-textarea-container">
                    {renderLyricsTextAreaActions()}
                    <textarea
                      rows="12"
                      name="currentLyrics"
                      id="currentLyrics"
                      value={data.currentLyrics}
                      placeholder={lyricsPlaceholder}
                      onChange={handleLyricsChange}
                      onKeyDown={handleKeyDown}
                      className={`mg-form-control ${writingBarChart ? 'bar-chart' : ''}`}
                    ></textarea>
                    <ThemeSuggestions
                      allThemes={allThemes}
                      currentThemes={data.themes || []}
                      suggestedThemes={suggestedThemes}
                      onThemeClick={handleSuggestedThemeClick}
                    />
                    {errors.currentLyrics && (
                      <small className="mg-form-text mg-form-text--error">{errors.currentLyrics}</small>
                    )}
                  </div>
                </div>
              </div>

              <div className={cx('mg-col-lg-3', { 'tab-hidden': mobileTab !== 'parts' })}>
                <div className="song-form__parts-header">
                  <p className="section-header">{t('songForm.parts')}</p>
                  <div className="song-form__parts-header-actions">
                    <button
                      className={`song-parts-intensity-btn${arranging ? ' active' : ''}`}
                      onClick={() => {
                        setArranging((on) => !on);
                        setArrangeIds([]);
                      }}
                      type="button"
                      title={t('songForm.intensity')}
                      disabled={!hasParts || !!editPartId}
                    >
                      <Icon icon={<IntensityIcon />} />
                    </button>
                    <button
                      className={`song-parts-overview-btn${overview ? ' active' : ''}`}
                      onClick={() => setOverview((v) => !v)}
                      type="button"
                      title={t('view.overview')}
                      disabled={!hasParts}
                    >
                      <Icon icon={<PageStarIcon />} />
                    </button>
                    <button
                      className="song-parts-grid-btn"
                      onClick={handleOpenGridModal}
                      type="button"
                      title={t('songForm.gridView')}
                      disabled={!hasParts}
                    >
                      <Icon icon={<GridIcon />} />
                    </button>
                  </div>
                </div>
                {arranging && !editPartId && (
                  <IntensityPanel
                    parts={data.parts}
                    partNumbers={computePartNumbers(data.parts)}
                    selectedIds={arrangeIds}
                    song={{
                      bpm: data.bpm,
                      timeSignature: data.timeSignature,
                      chartInstrumentId: data.chartInstrumentId,
                      chartRhythm: data.chartRhythm,
                      chartEffects: data.chartEffects,
                      category: categories.find((category) => category._id === data.categoryId)?.name,
                      key: data.key,
                      sourceKey: loadedSongKey,
                    }}
                    onEffectsChange={handleEffectsChange}
                    onRhythmChange={(choice) => arrangeEdit(withRhythm(choice))}
                    onInstrumentChange={(id) => arrangeEdit(withInstrument(id))}
                    onUndo={() => stepArrange(undoHistory)}
                    onRedo={() => stepArrange(redoHistory)}
                    canUndo={arrangeHistory.past.length > 0}
                    canRedo={arrangeHistory.future.length > 0}
                    onToggle={toggleArrangeId}
                    onSelect={setArrangeIds}
                    onApply={handleArrange}
                    onReset={handleArrangeReset}
                    onSuggest={handleArrangeSuggest}
                    onDone={() => {
                      setArranging(false);
                      setArrangeIds([]);
                    }}
                  />
                )}
                <SongParts
                  editPartId={editPartId}
                  parts={data.parts}
                  overview={overview}
                  onPartMove={handlePartMove}
                  onPartDelete={handlePartDeleteWithFlash}
                  onPartDuplicate={handlePartDuplicate}
                  onPartEdit={handlePartEdit}
                  onPartIntensity={openInIntensity}
                  onPartUpdate={handlePartUpdate}
                  onPartSort={handlePartSortRequest}
                  flashingPartId={flashingPart?.id}
                  flashingType={flashingPart?.type}
                  refreshKey={listRefreshKey}
                  onToggleAttach={handleToggleAttach}
                  timeSignature={data.timeSignature}
                />
              </div>
            </div>
          </form>
        </div>
      )}
      <Footer marginBottom={12} />

      <SongPartsGridModal
        isOpen={isGridModalOpen}
        onClose={handleCloseGridModal}
        parts={data.parts}
        onPartDelete={handlePartDelete}
        onPartDuplicate={handlePartDuplicate}
        onPartEdit={handlePartEdit}
        onPartUpdate={handlePartUpdate}
        onPartSort={handlePartSort}
        editPartId={editPartId}
        overview={overview}
        timeSignature={data.timeSignature}
      />

      <PromptModal
        isOpen={!!pendingIntensityPart}
        onClose={() => setPendingIntensityPart(null)}
        message={
          pendingIntensityPart &&
          t('songForm.unsavedForIntensity', {
            part: `${partName(pendingIntensityPart.name)} ${pendingIntensityPart.number}`,
          })
        }
        primaryText={t('songForm.discardChanges')}
        onPrimaryAction={() => openInIntensity(pendingIntensityPart)}
        secondaryText={t('common.cancel')}
        isDestructive
      />

      <PromptModal
        isOpen={!!pendingMove}
        onClose={handleCancelMove}
        primaryText={t('songForm.movePart')}
        secondaryText={t('common.cancel')}
        message={
          pendingMove && (
            <div className="renumber-preview">
              <p className="renumber-preview__intro">
                {pendingMove.sectionName ? (
                  <Trans
                    i18nKey="songForm.moveIntoSection"
                    values={{ section: pendingMove.sectionName }}
                    components={{ section: <span className="renumber-preview__section" /> }}
                  />
                ) : (
                  t('songForm.moveRenumbers')
                )}
              </p>
              <ul className="renumber-preview__list">
                {pendingMove.changes.map((change) => {
                  const inOtherSection = !change.isMoved && change.sectionName !== pendingMove.sectionName;

                  return (
                    <li
                      key={change.id}
                      className={theme.renderPartColor(
                        change.name,
                        cx('renumber-preview__item', {
                          'renumber-preview__item--moved': change.isMoved,
                        }),
                      )}
                    >
                      <span className="renumber-preview__before">
                        {partName(change.name)} {change.from}
                      </span>
                      <span className="renumber-preview__arrow">→</span>
                      <span className="renumber-preview__after">
                        {partName(change.name)} {change.to}
                        {change.isDuplicate && (
                          <RepeatIcon className="duplicate-icon" aria-label={t('songForm.part.isDuplicate')} />
                        )}
                      </span>
                      {change.isMoved && <span className="renumber-preview__tag">{t('songForm.moved')}</span>}
                      {inOtherSection && (
                        <span className="renumber-preview__section">
                          {change.sectionName || t('songForm.noSection')}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )
        }
        onPrimaryAction={handleConfirmMove}
        autoCloseOnPrimary={false}
      />

      <PromptModal
        isOpen={!!pendingRepeatSpread}
        onClose={() => setPendingRepeatSpread(null)}
        primaryText={t('songForm.yes')}
        secondaryText={t('songForm.no')}
        onPrimaryAction={() => handleRepeatSpreadAnswer(true)}
        onSecondaryAction={() => handleRepeatSpreadAnswer(false)}
        message={
          pendingRepeatSpread && (
            <div className="repeat-spread-prompt">
              <p>
                <Trans
                  i18nKey="songForm.repeatSpread"
                  count={pendingRepeatSpread.duplicateCount}
                  values={{ part: pendingRepeatSpread.label, repeats: pendingRepeatSpread.repeatCount }}
                  components={{
                    part: <span className={theme.renderPartColor(pendingRepeatSpread.name, 'part')} />,
                    dup: <Duplicates />,
                    reps: <span className="repeats" />,
                  }}
                />
              </p>
            </div>
          )
        }
      />
      <PromptModal
        isOpen={!!pendingChartUpdate}
        onClose={() => setPendingChartUpdate(null)}
        primaryText={t('dialogs.chordBars.updateChart')}
        secondaryText={t('songForm.keepIt')}
        onPrimaryAction={() => handleChartUpdateAnswer(true)}
        onSecondaryAction={() => handleChartUpdateAnswer(false)}
        message={
          pendingChartUpdate && (
            <div className="chart-update-prompt">
              <p>
                <Trans
                  i18nKey="songForm.chartStale"
                  values={{ part: pendingChartUpdate.label }}
                  components={{ part: <span className={theme.renderPartColor(pendingChartUpdate.name, 'part')} /> }}
                />
              </p>
              <p>{t('songForm.chartStaleQuestion')}</p>
            </div>
          )
        }
      />
      <PromptModal
        isOpen={!!pendingLyricsChart}
        onClose={() => setPendingLyricsChart(null)}
        primaryText={t('songForm.replaceChart')}
        secondaryText={t('common.cancel')}
        onPrimaryAction={handleLyricsChartAnswer}
        onSecondaryAction={() => setPendingLyricsChart(null)}
        message={
          pendingLyricsChart && (
            <div className="chart-update-prompt">
              <p>
                <Trans
                  i18nKey="songForm.lyricsChart"
                  values={{ part: pendingLyricsChart.label }}
                  components={{ part: <span className={theme.renderPartColor(pendingLyricsChart.name, 'part')} /> }}
                />
              </p>
              <p>{t('songForm.lyricsChartQuestion')}</p>
            </div>
          )
        }
      />

      {isTimerModalOpen && (
        <RepeatTimerModal
          isOpen={isTimerModalOpen}
          onClose={() => setIsTimerModalOpen(false)}
          bpm={editingPartBpm}
          timeSignature={data.timeSignature}
          initialRepeatCount={getRepeatCount({ repeatCount: data.currentRepeatCount, lyrics: data.currentLyrics })}
          lyrics={data.currentLyrics}
          onInsert={handleInsertRepeatDirective}
        />
      )}

      {isChordBarsModalOpen && (
        <ChordBarsModal
          isOpen={isChordBarsModalOpen}
          onClose={() => setIsChordBarsModalOpen(false)}
          onInsert={handleInsertChordBars}
          onRemove={handleRemoveChordBars}
          partLabel={`${partName(data.currentPart)} ${data.partNumber}`}
          song={{
            key: data.key,
            bpm: editingPartBpm,
            timeSignature: data.timeSignature,
            category: categories.find((category) => category._id === data.categoryId)?.name,
            chartInstrumentId: data.chartInstrumentId || undefined,
            chartRhythm: data.chartRhythm || undefined,
            chartEffects: data.chartEffects,
          }}
          songBpm={data.bpm}
          partKey={chartKey}
          lyrics={data.currentLyrics}
          isAttached={isEditingAttachedChart}
          chart={pendingChart}
          instrumentId={chartInstrumentId}
          rhythm={chartRhythm}
          melody={chartMelody}
        />
      )}

      <AssignPartModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        part={assigningPart}
        teamUsers={teamUsers}
        instruments={instruments}
        onConfirm={handleAssignPart}
      />

      <PartMidiUploadModal
        isOpen={isMidiModalOpen}
        onClose={() => setIsMidiModalOpen(false)}
        part={midiPart}
        onConfirm={handleSetPartMidi}
        partKey={editingPartKey}
        soundingKey={editingSoundingKey}
        songKey={data.key}
        loadedSongKey={loadedSongKey}
        beatsPerBar={beatsPerBar}
        isAdmin={isAdmin}
      />

      <PromptModal
        isOpen={!!pendingKeyWarning}
        onClose={() => setPendingKeyWarning(null)}
        primaryText={t('songForm.changeAnyway')}
        secondaryText={t('common.cancel')}
        onPrimaryAction={handleConfirmKeyWarning}
        onSecondaryAction={() => setPendingKeyWarning(null)}
        message={
          <div className="midi-key-warning">
            <p>{t('songForm.midiKeyWarning')}</p>
            <p>{t('songForm.midiKeyUnchanged')}</p>
          </div>
        }
      />

      {SUBSCRIPTION_ENABLED && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          onUpgrade={() => {
            setShowUpgradeModal(false);
            props.history.push('/subscribe');
          }}
          currentCount={songCount ?? 0}
        />
      )}
    </>
  );
};

export default SongForm;
